<template>
  <div class="min-h-screen bg-background">
    <Header />

    <main>
      <!-- Hero Section -->
      <section class="relative overflow-hidden bg-bubba-cream py-12 md:py-16">
        <PawPrint class="absolute left-[5%] top-[10%] h-[10vw] w-[10vw] min-h-12 min-w-12 -rotate-15 text-bubba-green opacity-10" />
        <PawPrint class="absolute right-[8%] top-[15%] h-[14vw] w-[14vw] min-h-16 min-w-16 rotate-20 text-bubba-yellow opacity-10" />
        <PawPrint class="absolute bottom-[10%] left-[15%] h-[12vw] w-[12vw] min-h-14 min-w-14 rotate-35 text-bubba-orange opacity-8" />

        <div class="container relative">
          <div class="mx-auto max-w-3xl text-center">
            <h1 class="mb-4 text-4xl font-bold tracking-tight text-foreground md:text-5xl">
              {{ $t('pets.hero.title') }}
              <span class="text-bubba-green">{{ $t('pets.hero.titleHighlight') }}</span>
            </h1>
            <p class="text-lg text-muted-foreground">
              {{ $t('pets.hero.description') }}
            </p>
          </div>
        </div>
      </section>

      <!-- Filters Section -->
      <section class="border-b border-border bg-card py-6">
        <div class="container space-y-4">
          <!-- Listing Type Tabs -->
          <div class="flex items-center gap-1">
            <button
              v-for="tab in listingTabs"
              :key="tab.value"
              type="button"
              class="relative px-4 py-2 text-sm font-medium transition-colors"
              :class="activeType === tab.value
                ? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-bubba-green'
                : 'text-muted-foreground hover:text-foreground'"
              @click="activeType = tab.value"
            >
              {{ $t(`pets.tabs.${tab.key}`) }}
            </button>
          </div>

          <!-- Species Filter Pills -->
          <div class="flex flex-wrap items-center gap-2">
            <button
              v-for="sp in speciesOptions"
              :key="sp.value"
              type="button"
              class="rounded-full border px-4 py-1.5 text-sm font-medium transition-colors"
              :class="activeSpecies === sp.value
                ? 'border-bubba-green bg-bubba-green/10 text-bubba-green'
                : 'border-border bg-background text-muted-foreground hover:border-foreground/30 hover:text-foreground'"
              @click="activeSpecies = sp.value"
            >
              {{ $t(`pets.species.${sp.key}`) }}
            </button>
          </div>
        </div>
      </section>

      <!-- Listings Grid -->
      <section class="py-10">
        <div class="container">
          <!-- Results count -->
          <p v-if="!isLoading && listings.length > 0" class="mb-6 text-sm text-muted-foreground">
            {{ listings.length }} {{ $t('pets.resultsCount') }}
          </p>

          <Skeleton name="website-pets-list" :loading="isLoading">
            <!-- Listing Cards -->
            <div v-if="listings.length > 0" class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <NuxtLink
                v-for="item in listings"
                :key="item._id"
                :to="$localeRoute(`/pets/${item.slug}`)"
                class="group overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md"
              >
                <!-- Image -->
                <div class="relative aspect-[4/3] overflow-hidden bg-muted">
                  <img
                    v-if="item.imageUrls[0]"
                    :src="item.imageUrls[0]"
                    :alt="item.title"
                    class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  >
                  <div v-else class="flex h-full w-full items-center justify-center">
                    <Icon name="tabler:photo" size="48" class="text-muted-foreground/30" />
                  </div>

                  <!-- Type Badge -->
                  <span
                    class="absolute left-3 top-3 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
                    :class="item.listingType === 'sale' ? 'bg-blue-500' : 'bg-emerald-500'"
                  >
                    {{ item.listingType === 'sale' ? $t('pets.card.forSale') : $t('pets.card.forAdoption') }}
                  </span>
                </div>

                <!-- Info -->
                <div class="p-4">
                  <h3 class="mb-1 line-clamp-1 text-base font-semibold text-foreground group-hover:text-bubba-green">
                    {{ item.title }}
                  </h3>
                  <p class="mb-2 text-sm text-muted-foreground">
                    {{ item.petName }}
                    <span v-if="item.breedSlug"> &middot; {{ item.breedSlug }}</span>
                  </p>

                  <div class="flex items-center justify-between">
                    <span class="text-lg font-bold text-foreground">
                      {{ item.listingType === 'adoption' && item.price === 0
                        ? $ts('pets.card.free')
                        : formatCurrency(item.price, undefined, item.currency, 2) }}
                    </span>
                    <span
                      class="rounded-full bg-muted px-2 py-0.5 text-xs font-medium capitalize text-muted-foreground"
                    >
                      {{ item.species }}
                    </span>
                  </div>

                  <p v-if="item.location" class="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="tabler:map-pin" size="14" />
                    {{ item.location }}
                  </p>
                </div>
              </NuxtLink>
            </div>

            <!-- Empty State -->
            <div v-else class="py-20 text-center">
              <Icon name="tabler:paw-off" size="48" class="mx-auto mb-4 text-muted-foreground/50" />
              <h3 class="mb-2 text-lg font-semibold text-foreground">{{ $t('pets.empty.title') }}</h3>
              <p class="text-muted-foreground">{{ $t('pets.empty.description') }}</p>
            </div>
            <template #fallback>
              <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <div v-for="n in 8" :key="n" class="h-80 animate-pulse rounded-2xl bg-muted" />
              </div>
            </template>
          </Skeleton>
        </div>
      </section>
    </main>

    <Footer />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Skeleton from 'boneyard-js/vue';
import { useSeoMeta, useI18n, useHead, useSchemaOrg, defineWebPage, definePageMeta, useRoute, useRuntimeConfig } from '#imports';
import Header from '#components/base/Header.vue';
import Footer from '#components/base/Footer.vue';
import PawPrint from '#components/landing/PawPrint.vue';
import { createLogger } from '#bubba/shared/utils/logger';
import { useBreadcrumbSchema } from '#composables/useBreadcrumbSchema';
import { useConvexQuery } from '#composables/useConvex';
import { api } from '@convex/_generated/api';
import { formatCurrency } from '#bubba/shared/utils/currency';

const log = createLogger('PetsBrowse');

definePageMeta({ layout: false });

const { $t, $ts, $getLocale, $localeRoute } = useI18n();
const { public: { siteUrl } } = useRuntimeConfig();
const route = useRoute();
const locale = computed(() => $getLocale(route));

const canonicalUrl = computed(() => {
  const prefix = locale.value === 'en' ? '' : `/${locale.value}`;
  return `${siteUrl}${prefix}/pets`;
});

// ============================================================================
// Filters
// ============================================================================

const listingTabs = [
  { key: 'all', value: '' },
  { key: 'sale', value: 'sale' },
  { key: 'adoption', value: 'adoption' },
] as const;

const speciesOptions = [
  { key: 'all', value: '' },
  { key: 'dog', value: 'dog' },
  { key: 'cat', value: 'cat' },
  { key: 'bird', value: 'bird' },
  { key: 'rabbit', value: 'rabbit' },
  { key: 'hamster', value: 'hamster' },
  { key: 'guinea_pig', value: 'guinea_pig' },
  { key: 'fish', value: 'fish' },
  { key: 'turtle', value: 'turtle' },
  { key: 'snake', value: 'snake' },
  { key: 'lizard', value: 'lizard' },
  { key: 'ferret', value: 'ferret' },
  { key: 'horse', value: 'horse' },
  { key: 'other', value: 'other' },
] as const;

const activeType = ref('');
const activeSpecies = ref('');

// ============================================================================
// Data
// ============================================================================

const { data: rawListings, isLoading } = useConvexQuery(
  api.petListings.queries.browseListings,
  computed(() => {
    const args: Record<string, any> = {};
    if (activeSpecies.value) args.species = activeSpecies.value;
    if (activeType.value) args.listingType = activeType.value;
    args.numItems = 40;
    return args;
  }),
);

const listings = computed(() => rawListings.value ?? []);

// ============================================================================
// SEO
// ============================================================================

useSeoMeta({
  title: () => $ts('pets.seo.title'),
  description: () => $ts('pets.seo.description'),
  ogTitle: () => $ts('pets.seo.title'),
  ogDescription: () => $ts('pets.seo.description'),
  ogType: 'website',
  ogUrl: () => canonicalUrl.value,
  twitterCard: 'summary_large_image',
  twitterTitle: () => $ts('pets.seo.title'),
  twitterDescription: () => $ts('pets.seo.description'),
});

useHead({
  link: [{ rel: 'canonical', href: () => canonicalUrl.value }],
});

useSchemaOrg([defineWebPage({ '@type': 'CollectionPage' })]);

useBreadcrumbSchema([
  { name: 'Home', item: '/' },
  { name: 'Pets', item: '/pets' },
]);
</script>
